import { createSlice } from '@reduxjs/toolkit';
import { normalizeMockupLogo, normalizeMockupLogos } from '@/domain/mockups/mockupLogos';
const DEFAULT_RESOLUTION = { x: 1024, y: 1024 };
const context = { scene: null, styleScene: null };
export function getSceneDesc() {
    return context.scene;
}
export function getStyleSceneDesc() {
    return context.styleScene;
}
export function setSceneDesc(scene) {
    context.scene = scene;
}
export function setStyleSceneDesc(scene) {
    context.styleScene = scene;
}
const initialState = {
    hoverGraphicId: null,
    mockupHideTearAwayLabel: false,
    mockupLogos: [],
    pointsOfMeasure: [],
    recipeLogoBaseline: null,
    recipeSource: null,
    colorway: null,
    sessionToken: null,
    sessionTokenExpiresAt: null,
    resolution: Object.assign({}, DEFAULT_RESOLUTION),
    sceneIsLoading: false,
    sceneVersion: 0,
    selectedGraphicId: null,
    style: null,
    styleNotFoundMessage: null,
    stylePreviewUrl: null,
    styleRegions: [],
};
const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        clear: state => {
            state.hoverGraphicId = null;
            state.mockupHideTearAwayLabel = false;
            state.mockupLogos = [];
            state.pointsOfMeasure = [];
            state.recipeLogoBaseline = null;
            state.recipeSource = null;
            state.colorway = null;
            state.sessionToken = null;
            state.sessionTokenExpiresAt = null;
            state.resolution = Object.assign({}, DEFAULT_RESOLUTION);
            state.sceneIsLoading = false;
            state.sceneVersion = 0;
            state.selectedGraphicId = null;
            state.style = null;
            state.styleNotFoundMessage = null;
            state.stylePreviewUrl = null;
            state.styleRegions = [];
        },
        clearRecipeExportState: state => {
            state.pointsOfMeasure = [];
            state.recipeLogoBaseline = null;
            state.recipeSource = null;
            state.colorway = null;
            state.sessionToken = null;
            state.sessionTokenExpiresAt = null;
        },
        setRecipeExportState: (state, action) => {
            state.recipeSource = action.payload.recipeSource;
            state.recipeLogoBaseline = normalizeMockupLogos(action.payload.recipeLogoBaseline);
            state.pointsOfMeasure = action.payload.pointsOfMeasure;
            if (action.payload.colorway !== undefined) {
                state.colorway = action.payload.colorway;
            }
            if (action.payload.sessionToken !== undefined) {
                state.sessionToken = action.payload.sessionToken;
            }
            if (action.payload.sessionTokenExpiresAt !== undefined) {
                state.sessionTokenExpiresAt = action.payload.sessionTokenExpiresAt;
            }
        },
        setResolution: (state, action) => {
            state.resolution = action.payload;
        },
        setSceneIsLoading: (state, action) => {
            state.sceneIsLoading = action.payload;
        },
        setStyle: (state, action) => {
            state.style = action.payload;
        },
        setStylePreviewUrl: (state, action) => {
            state.stylePreviewUrl = action.payload;
        },
        setStyleNotFoundMessage: (state, action) => {
            state.styleNotFoundMessage = action.payload;
        },
        updateScene: state => {
            state.sceneVersion = state.sceneVersion + 1;
        },
        setMockupLogos: (state, action) => {
            state.mockupLogos = normalizeMockupLogos(action.payload.logos);
            state.mockupHideTearAwayLabel = action.payload.hideTearAwayLabel;
        },
        clearMockupLogos: state => {
            state.mockupLogos = [];
            state.mockupHideTearAwayLabel = false;
            state.selectedGraphicId = null;
        },
        setSelectedGraphicId: (state, action) => {
            state.selectedGraphicId = action.payload;
        },
        setHoverGraphicId: (state, action) => {
            state.hoverGraphicId = action.payload;
        },
        updateGraphicTransform: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.transform.translation = action.payload.translation;
            }
        },
        setGraphicTransform: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.transform = action.payload.transform;
            }
        },
        updateGraphicDisplayName: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.displayName = action.payload.displayName;
            }
        },
        updateGraphicRegion: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.region = action.payload.region;
            }
        },
        updateGraphicSize: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.size = action.payload.size;
            }
        },
        updateGraphicTechnique: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.technique = action.payload.technique;
            }
        },
        updateGraphicCustomTechnique: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.customTechnique = action.payload.customTechnique;
            }
        },
        removeGraphic: (state, action) => {
            state.mockupLogos = state.mockupLogos.filter(l => l.id !== action.payload);
            if (state.selectedGraphicId === action.payload) {
                state.selectedGraphicId = null;
            }
        },
        addGraphic: (state, action) => {
            state.mockupLogos = [...state.mockupLogos, normalizeMockupLogo(action.payload)];
            state.selectedGraphicId = action.payload.id;
        },
        replaceGraphicImage: (state, action) => {
            const logo = state.mockupLogos.find(l => l.id === action.payload.id);
            if (logo) {
                logo.imageSrc = action.payload.imageSrc;
                logo.imageSize = action.payload.imageSize;
                logo.size = action.payload.size;
                if (action.payload.sourceUrl !== undefined) {
                    logo.sourceUrl = action.payload.sourceUrl;
                }
                logo.recoloredImageSrc = undefined;
                logo.recoloredId = undefined;
            }
        },
        setStyleRegions: (state, action) => {
            state.styleRegions = action.payload;
        },
    },
});
export const { addGraphic, clear, clearMockupLogos, clearRecipeExportState, removeGraphic, replaceGraphicImage, setGraphicTransform, setHoverGraphicId, setMockupLogos, setRecipeExportState, setResolution, setSceneIsLoading, setSelectedGraphicId, setStyle, setStyleNotFoundMessage, setStylePreviewUrl, setStyleRegions, updateGraphicCustomTechnique, updateGraphicDisplayName, updateGraphicRegion, updateGraphicSize, updateGraphicTechnique, updateGraphicTransform, updateScene, } = appSlice.actions;
export const appReducer = appSlice.reducer;
